import React, { memo } from "react";
import { AddToNichePopup } from "./AddToNichePopup";
import { useNicheData } from "./utils/useNicheData";
import { apiNiches } from "@/common/services";
import { INicheOption } from "./types";
import { IAddToNichePopupProps } from "@/common/buses/popups/popup-types";
import { onCloseAddNichePopup, onOpenAddToNicheSuccessPopup } from "@/components/popup-dispatcher/PopupDispatcher";
import { showNotification } from "@/components/notifications/Notifications";
import { AxiosError } from "axios";
import { IErrorData } from "@/common/services/types";

export const AddToNichePopupContainer: React.FC<IAddToNichePopupProps> = memo(({
  gifId,
  tags,
  preferences = [],
}) => {
  const {
    nicheFields,
    suggestNicheFields,
    handleSetSuggestedNicheFiled,
    handleSetNicheFiled,
    myNichesLoading,
    gifSuggestedNichesLoading
  } = useNicheData({ gifId, tags, preferences });

  const onClosePopup = () => {
    onCloseAddNichePopup();
  };

  const onFollowNiche = async (suggestedNiche: INicheOption) => {
    const nicheIndx = suggestNicheFields.findIndex((n) => n.id === suggestedNiche.id);
    const updatedSuggested = [...suggestNicheFields].filter((n, indx) => indx !== nicheIndx);

    try {
      await apiNiches.follow(suggestedNiche.id);

      handleSetNicheFiled([...nicheFields, suggestedNiche]);
      handleSetSuggestedNicheFiled(updatedSuggested);
      showNotification({
        title: "Joined!",
        description: "You can now add content to this Niche",
        variant: "success",
      });
    } catch {
      showNotification({
        title: "Could not change niches status",
        description: "Please try again later.",
        variant: "error",
      });
      console.error("Could not change niches status.");
    }
  };

  const onSubmit = async (niches: string[]) => {
    try {
      await apiNiches.addGifNiches({
        gifId: gifId,
        nicheIds: niches
      });

      onCloseAddNichePopup();
      onOpenAddToNicheSuccessPopup();
    } catch (err) {
      console.error("Could not set gif niches", err);

      showNotification({
        title: "Unable to add to niche",
        description: (err as AxiosError<IErrorData>).response?.data?.error?.message ||
            "Could not add gifs to selected niches, please try again later",
        variant: "error",
        actionButton: {
          title: "Go to the FAQ",
          onClick: () => window.open("https://help.redgifs.com/hc/en-us/sections/18029718713745-Niche-Management", "_blank"),
        },
      });
    }
  };

  return (
    <AddToNichePopup
      nicheFields={nicheFields}
      suggestNicheFields={suggestNicheFields}
      myNichesLoading={myNichesLoading}
      gifSuggestedNichesLoading={gifSuggestedNichesLoading}
      onUpdateNiches={handleSetNicheFiled}
      onClosePopup={onClosePopup}
      onSubmit={onSubmit}
      onFollowNiche={onFollowNiche}
      tags={tags}
      preferences={preferences}
    />
  );
});
